// React Component for Viral Automation Dashboard
// File: frontend/src/components/ViralAutomationPanel.jsx

import React, { useState, useEffect } from 'react';
import { getCatalogAnalytics } from '../services/AnalyticsService';
import { useAuth } from '../context/AuthContext';

const ViralAutomationPanel = () => {
  const { artistData } = useAuth();
  const [socialPosts, setSocialPosts] = useState([]);
  const [automationStatus, setAutomationStatus] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [catalogData, setCatalogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [artistData]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get real data from catalog analytics
      const artistId = artistData?.id || 'rdv01';
      const analytics = await getCatalogAnalytics(artistId);
      setCatalogData(analytics);
      
      // Generate promotional social posts for decodedmusic.com and Rue de Vivre
      if (analytics && analytics.tracks) {
        const mockPosts = analytics.tracks.slice(0, 5).map((track, index) => ({
          id: `post_${index + 1}`,
          track_id: track.track_id,
          title: track.title,
          content: `🎵 Discover "${track.title}" by independent artist Rue de Vivre. ${track.vibe || 'Powerful'} vibes perfect for your playlist. Listen now and explore more at www.decodedmusic.com 🎧 #IndieMusic #NewMusic #RueDeVivre`,
          scheduled_time: new Date(Date.now() + (index + 1) * 24 * 60 * 60 * 1000).toISOString(),
          platforms: track.platforms || ['facebook', 'instagram', 'twitter'],
          status: index === 0 ? 'published' : index === 1 ? 'scheduled' : 'draft',
          engagement_score: Math.floor(Math.random() * 100),
          spotify_url: track.spotify_url,
          viral_score: track.viralScore || Math.floor(Math.random() * 100)
        }));
        setSocialPosts(mockPosts);
      }
      
      // Mock automation status
      setAutomationStatus({
        active: true,
        posts_scheduled: 12,
        posts_published: 8,
        engagement_rate: 4.2,
        reach: 15420,
        last_post: new Date().toISOString()
      });
      
      // Mock weekly report
      setWeeklyReport({
        total_posts: 7,
        total_engagement: 1240,
        top_performing_track: analytics?.tracks?.[0]?.title || 'Unknown',
        viral_candidates: analytics?.tracks?.filter(t => (t.viralScore || 0) > 70).length || 2
      });
      
    } catch (err) {
      console.error('Error loading viral automation data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishPost = async (postId) => {
    try {
      // You would get these from your user settings or environment
      const accessToken = process.env.REACT_APP_FB_ACCESS_TOKEN;
      const pageId = process.env.REACT_APP_FB_PAGE_ID;
      
      await ViralAutomationAPI.publishSocialPost(postId, accessToken, pageId);
      await loadData(); // Refresh data
      alert('Post published successfully!');
    } catch (err) {
      alert(`Failed to publish: ${err.message}`);
    }
  };

  const handleTriggerViralGeneration = async () => {
    try {
      await ViralAutomationAPI.triggerViralGeneration();
      alert('Viral content generation triggered!');
      await loadData();
    } catch (err) {
      alert(`Failed to trigger: ${err.message}`);
    }
  };

  const handleLoadCSV = async () => {
    try {
      const result = await ViralAutomationAPI.loadCSVContent();
      alert(`CSV loaded: ${result.newPosts} new posts created`);
      await loadData();
    } catch (err) {
      alert(`Failed to load CSV: ${err.message}`);
    }
  };

  if (loading) return <div>Loading viral automation data...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="viral-automation-panel">
      <h2>🚀 Content Automation Dashboard</h2>
      
      {/* Automation Status */}
      <div className="automation-status">
        <h3>📊 System Status</h3>
        {automationStatus && (
          <div className="status-grid">
            <div className="status-item">
              <span>EventBridge Rules:</span>
              <span className="status-value">{automationStatus.eventBridgeRules} Active</span>
            </div>
            <div className="status-item">
              <span>Lambda Functions:</span>
              <span className="status-value">{automationStatus.lambdaFunctions} Deployed</span>
            </div>
            <div className="status-item">
              <span>Total Posts:</span>
              <span className="status-value">{socialPosts.length}</span>
            </div>
          </div>
        )}
      </div>

      {/* Manual Controls */}
      <div className="manual-controls">
        <h3>🎯 Manual Controls</h3>
        <div className="control-buttons">
          <button onClick={handleTriggerViralGeneration} className="btn-viral">
            🔥 Generate Viral Content
          </button>
          <button onClick={handleLoadCSV} className="btn-csv">
            📋 Load CSV Content
          </button>
          <button onClick={loadData} className="btn-refresh">
            🔄 Refresh Data
          </button>
        </div>
      </div>

      {/* Weekly Report Summary */}
      {weeklyReport && (
        <div className="weekly-report">
          <h3>📈 This Week's Summary</h3>
          <div className="report-grid">
            <div className="report-item">
              <span>Posts Created:</span>
              <span>{weeklyReport.postsCreated}</span>
            </div>
            <div className="report-item">
              <span>Posts Published:</span>
              <span>{weeklyReport.postsPublished}</span>
            </div>
            <div className="report-item">
              <span>Viral Score Avg:</span>
              <span>{weeklyReport.avgViralScore}</span>
            </div>
          </div>
        </div>
      )}

      {/* Social Posts Table */}
      <div className="social-posts">
        <h3>📱 Social Posts</h3>
        <div className="posts-table">
          <table>
            <thead>
              <tr>
                <th>Content</th>
                <th>Platform</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {socialPosts.map(post => (
                <tr key={post.id}>
                  <td>
                    <div className="post-content">
                      <strong>{post.randomTagline}</strong>
                      <br />
                      <small>{post.randomDescription?.substring(0, 100)}...</small>
                    </div>
                  </td>
                  <td>
                    <span className={`platform-badge ${post.platform}`}>
                      {post.platform}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${post.postingStatus.toLowerCase().replace(' ', '-')}`}>
                      {post.postingStatus}
                    </span>
                  </td>
                  <td>{new Date(post.createdAt).toLocaleDateString()}</td>
                  <td>
                    {post.postingStatus === 'Ready to Post' && (
                      <button 
                        onClick={() => handlePublishPost(post.id)}
                        className="btn-publish"
                      >
                        🚀 Publish
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <style jsx>{`
        .viral-automation-panel {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .status-grid, .report-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin: 15px 0;
        }
        
        .status-item, .report-item {
          display: flex;
          justify-content: space-between;
          padding: 10px;
          background: #f5f5f5;
          border-radius: 5px;
        }
        
        .control-buttons {
          display: flex;
          gap: 10px;
          margin: 15px 0;
        }
        
        .btn-viral, .btn-csv, .btn-refresh, .btn-publish {
          padding: 10px 20px;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
        }
        
        .btn-viral { background: #ff4757; color: white; }
        .btn-csv { background: #3742fa; color: white; }
        .btn-refresh { background: #2ed573; color: white; }
        .btn-publish { background: #ffa502; color: white; }
        
        .posts-table table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 15px;
        }
        
        .posts-table th, .posts-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #ddd;
        }
        
        .platform-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: bold;
        }
        
        .platform-badge.facebook { background: #4267B2; color: white; }
        .platform-badge.instagram { background: #E4405F; color: white; }
        .platform-badge.twitter { background: #1DA1F2; color: white; }
        
        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: bold;
        }
        
        .status-badge.ready-to-post { background: #2ed573; color: white; }
        .status-badge.posted { background: #5352ed; color: white; }
        .status-badge.failed { background: #ff3838; color: white; }
      `}</style>
    </div>
  );
};

export default ViralAutomationPanel;
